import { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { SETTINGS_SCHEMA } from '../config/settings';
import RoomDiagnosticsSettings from '../components/RoomDiagnosticsSettings';
import {
  DEV_TOOLS_CHANGED_EVENT,
  DEV_TOOLS_ENABLED,
  FAKE_LOADING_SECONDS_KEY,
  FAKE_PC_TILES_KEY,
  FAKE_RUN_SECONDS_KEY,
  MOCK_LAUNCH_KEY,
  fakePcTilesActive,
  readFakeLoadingSeconds,
  readFakeRunSeconds,
  supabaseProjectRef,
} from '../utils/env';
import '../styles/settings-view.css';

interface Props {
  // Currently-selected room (from the top-left room picker). When present, the
  // admin gets a room-scoped policy section below the device-only toggles.
  roomId?: string;
  roomName?: string;
}

export default function SettingsView({ roomId, roomName }: Props) {
  const { settings, setSetting } = useSettings();

  return (
    <div className="settings-view">
      <div className="settings-header">
        <h2>Settings</h2>
        <p className="settings-hint">The toggles below apply to this device only.</p>
      </div>

      <div className="settings-list">
        {SETTINGS_SCHEMA.map((def) => {
          if (def.type === 'volume') {
            const disabled = !settings[def.enabledBy];
            const value = settings[def.key] as number;
            return (
              <label
                className={`settings-item settings-item--static${disabled ? ' settings-item--disabled' : ''}`}
                key={def.key}
                htmlFor={`setting-${def.key}`}
              >
                <div className="settings-item-text">
                  <div className="settings-item-label">{def.label}</div>
                  <div className="settings-item-desc">{def.description}</div>
                </div>
                <div className="settings-volume">
                  <input
                    id={`setting-${def.key}`}
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => setSetting(def.key, Number(e.target.value))}
                  />
                  <span className="settings-volume-value">{value}%</span>
                </div>
              </label>
            );
          }
          // A boolean row may hang off another switch (the queue pop-up rule
          // only means anything while the queue is on).
          const boolDisabled = def.enabledBy ? !settings[def.enabledBy] : false;
          return (
            <label
              className={`settings-item${boolDisabled ? ' settings-item--disabled' : ''}`}
              key={def.key}
              htmlFor={`setting-${def.key}`}
            >
              <div className="settings-item-text">
                <div className="settings-item-label">{def.label}</div>
                <div className="settings-item-desc">{def.description}</div>
              </div>
              <span className="settings-toggle">
                <input
                  id={`setting-${def.key}`}
                  type="checkbox"
                  checked={settings[def.key] as boolean}
                  disabled={boolDisabled}
                  onChange={(e) => setSetting(def.key, e.target.checked)}
                />
                <span className="settings-toggle-slider" aria-hidden="true" />
              </span>
            </label>
          );
        })}
      </div>

      {roomId && <RoomDiagnosticsSettings roomId={roomId} roomName={roomName ?? roomId} />}

      {DEV_TOOLS_ENABLED && <StagingTools />}
    </div>
  );
}

// Test hooks for the queue drill (STAGING.md). Only rendered when the bundle
// is a dev server or points at the staging project (DEV_TOOLS_ENABLED); the
// values live in localStorage, not in the device settings, so they never
// travel with a settings export and cannot exist in a prod build.
function StagingTools() {
  const [mockLaunch, setMockLaunch] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(MOCK_LAUNCH_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [fakeTiles, setFakeTiles] = useState<boolean>(() => fakePcTilesActive());
  const [fakeRunSeconds, setFakeRunSeconds] = useState<number>(() => readFakeRunSeconds());
  const [fakeLoadingSeconds, setFakeLoadingSeconds] = useState<number>(() => readFakeLoadingSeconds());

  function notify() {
    window.dispatchEvent(new Event(DEV_TOOLS_CHANGED_EVENT));
  }

  function writeFlag(key: string, on: boolean) {
    try {
      if (on) window.localStorage.setItem(key, '1');
      else window.localStorage.removeItem(key);
    } catch {
      // localStorage unavailable: nothing to persist.
    }
  }

  function toggleMock(on: boolean) {
    writeFlag(MOCK_LAUNCH_KEY, on);
    setMockLaunch(on);
    notify();
  }

  function toggleFakeTiles(on: boolean) {
    writeFlag(FAKE_PC_TILES_KEY, on);
    setFakeTiles(on);
    notify();
  }

  // Seconds fields: clamp to a sane range; an empty/invalid field falls back
  // to the reader's default on the next read.
  function applySeconds(key: string, raw: number, set: (n: number) => void) {
    const n = Math.max(1, Math.min(3600, Math.floor(Number.isFinite(raw) ? raw : 0)));
    try {
      window.localStorage.setItem(key, String(n));
    } catch {
      // localStorage unavailable: nothing to persist.
    }
    set(n);
    notify();
  }

  return (
    <div className="settings-room settings-staging">
      <div className="settings-room-header">
        <h3>Staging tools</h3>
        <p className="settings-hint">
          Test hooks for the queue drill. Visible because this build talks to{' '}
          {supabaseProjectRef() ?? 'a dev'} project; a prod bundle never shows this block.
        </p>
      </div>
      <div className="settings-list">
        <label className="settings-item" htmlFor="staging-mock-launch">
          <div className="settings-item-text">
            <div className="settings-item-label">Mock launch / stop (MOCK_LAUNCH)</div>
            <div className="settings-item-desc">
              Launch and stop calls succeed without contacting a PC agent, so the queue pop,
              undo-with-stop and the tile recovery actions can be exercised on a tile whose
              agent is unreachable. Stored in localStorage on this device.
            </div>
          </div>
          <span className="settings-toggle">
            <input
              id="staging-mock-launch"
              type="checkbox"
              checked={mockLaunch}
              onChange={(e) => toggleMock(e.target.checked)}
            />
            <span className="settings-toggle-slider" aria-hidden="true" />
          </span>
        </label>
        <label className="settings-item" htmlFor="staging-fake-tiles">
          <div className="settings-item-text">
            <div className="settings-item-label">Fake PC tiles (FAKE_PC_TILES)</div>
            <div className="settings-item-desc">
              Every PC of the selected room WITHOUT an IP address (the staging isolation script
              wipes venue addresses; DEV-01 keeps its address and stays a real tile) renders as a
              simulated tile with its own in-memory agent: idle, loading, running, done, with the
              visitor from the queue, the recovery actions and a Vastloper / Crash drill footer.
              No agent, no HTTP; the real pc_instances ids are recorded as started_pc_id.
            </div>
          </div>
          <span className="settings-toggle">
            <input
              id="staging-fake-tiles"
              type="checkbox"
              checked={fakeTiles}
              onChange={(e) => toggleFakeTiles(e.target.checked)}
            />
            <span className="settings-toggle-slider" aria-hidden="true" />
          </span>
        </label>
        <label className="settings-item settings-item--static" htmlFor="staging-fake-run-seconds">
          <div className="settings-item-text">
            <div className="settings-item-label">Fake run seconds</div>
            <div className="settings-item-desc">
              How long a fake tile shows the experience running before it finishes. The
              &quot;bijna klaar&quot; signal (Volgende banner + display overlay) fires 15 s before
              the end. Applies to the next launch.
            </div>
          </div>
          <div className="settings-volume">
            <input
              id="staging-fake-run-seconds"
              type="number"
              min={1}
              max={3600}
              value={fakeRunSeconds}
              onChange={(e) => applySeconds(FAKE_RUN_SECONDS_KEY, Number(e.target.value), setFakeRunSeconds)}
            />
          </div>
        </label>
        <label className="settings-item settings-item--static" htmlFor="staging-fake-loading-seconds">
          <div className="settings-item-text">
            <div className="settings-item-label">Fake loading seconds</div>
            <div className="settings-item-desc">
              How long a fake tile stays in &quot;Laden...&quot; after a launch. Applies to the
              next launch.
            </div>
          </div>
          <div className="settings-volume">
            <input
              id="staging-fake-loading-seconds"
              type="number"
              min={1}
              max={3600}
              value={fakeLoadingSeconds}
              onChange={(e) => applySeconds(FAKE_LOADING_SECONDS_KEY, Number(e.target.value), setFakeLoadingSeconds)}
            />
          </div>
        </label>
        <div className="settings-item settings-item--static">
          <div className="settings-item-text">
            <div className="settings-item-label">Console: window.dispatchDebug</div>
            <div className="settings-item-desc">
              On the dashboard (queue on), DevTools exposes window.dispatchDebug with
              report(pcId, {'{'}pcName, online, running, canLaunch{'}'}), free(pcId) and
              retract(pcId) to script PC transitions by hand. The NextUpWidget also shows
              &quot;Test Volgende&quot; / &quot;Test Start&quot; in this build.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
